/**
 * 딜등록 API 클라이언트 — backend `/deal-registrations/*` 와 1:1 (v2.140.0)
 *
 * 진행 중/종료·마감까지 남은 날·종료 연도는 **서버가 계산해 준다**(isClosed · dday · closedYear).
 * 화면은 그 값을 그대로 쓴다 — 판정을 여기 베끼지 않는다.
 */

import { api } from './api';
import type { RichDoc } from './rich-doc';

/** ⭐서버(rules.ts DEAL_STATUSES)와 **같은 목록**이어야 한다 — 여기만 고치면 typecheck 가 안 잡는다 */
export type DealStatus =
  | 'Pending' | 'On-going' | 'Extend' | 'Processing' | 'Won' | 'Lost' | 'Invalid' | 'Rejected';

/**
 * 딜등록 프로젝트 정보 업데이트 한 줄 (v2.194.0)
 * — 「업데이트 될 경우 별도의 칸에 새로 적을수 있게」. 옛 딜의 경과 줄은 projectInfo 글자 칸에 그대로 있다.
 */
export interface DealUpdate {
  id: number;
  /** YYYY-MM-DD */
  date: string;
  body: string;
  /** 적은 사람 로그인 id */
  by: string;
  /** 적은 사람 이름 — 화면에만 보인다(메일에는 안 나간다) */
  byName: string;
}

/** 딜등록 제안품목 한 줄 — 품목 칸 · 수량 칸 · 비고 칸 (v2.178.0) */
export interface DealItem {
  /** 품목 — `P/N  (이름)`. 비어 있으면 비고만 있는 메모 줄 */
  item: string;
  /** 수량 — 메모 줄은 0 */
  qty: number;
  /** 비고 — 가격·메모 같은 곁글 */
  note: string;
}

export interface DealRow {
  id: number;
  vendor: string;
  registrationNo: string;
  projectId: string;
  status: DealStatus;
  statusLabel: string;
  isClosed: boolean;
  appliedDate: string | null;
  expireDate: string | null;
  deadlineDate: string | null;
  lastUpdate: string | null;
  closedDate: string | null;
  closedYear: number | null;
  /** 마감까지 남은 날(오늘 기준 · 음수면 지남). 마감이 없으면 null */
  dday: number | null;
  /**
   * 종료 탭에 있나 (v2.215.0) — 사람이 [종료 탭으로 넘기기]로 넘긴 딜. **상태(isClosed)와 별개**다:
   * 실주·무효로 바꿔도 넘기기 전까지는 진행 중 탭에 남는다(24일 검토 표에 바뀐 상태로 나오게).
   */
  archived: boolean;
  archivedAt: string | null;
  archivedBy: string;
  tier2: string;
  /** 엔드유저 표준 글자 — 세 칸을 이어 붙인 것. **서버가 만든다**(직접 고치지 말 것) */
  endUser: string;
  /** 엔드유저 세 칸 (v2.193.0) — 한글만 필수, 영문·홈페이지는 선택 */
  endUserKo: string;
  endUserEn: string;
  endUserUrl: string;
  projectInfo: string;
  /** 프로젝트명(Topic) + 영문 — 인포트렌드 메일에 「한글 / English」로 나란히 들어간다 (v2.189.0) */
  topic: string;
  topicEn: string;
  projectInfoEn: string;
  pn: string;
  /** 품목 줄 — 품목 칸·수량 칸 (v2.178.0). 이쪽이 원본이고 pn 은 서버가 여기서 만든 사본 */
  items: DealItem[];
  /** 프로젝트 정보 업데이트 이력 — 오래된 것이 앞 (v2.194.0). 옛 경과 줄은 projectInfo 글자 칸에 있다 */
  updates: DealUpdate[];
  /** 위 이력을 「260918 : 내용」 줄로 이어 붙인 글 — 엑셀이 쓴다(서버가 만든다) */
  updatesText: string;
  remark: string;
  application: string;
  empId: string;
  ownerName: string;
  note: string;
  /** 딜 금액(USD) — 안 적었으면 null (0 과 구별한다 · v2.145.0) */
  amountUsd: number | null;
  /** 엑셀 이관 출처('xlsx:2026:R57') — 포털에서 넣은 줄은 '' */
  sourceRef: string;
  /** 제안사에 만료 안내 메일을 마지막으로 보낸 날 — 안 보냈으면 null */
  expiryNoticeAt: string | null;
  /** 이 딜과 연결된 정보 요청 메일을 마지막으로 보낸 날 — 없으면 null (v2.141.0) */
  infoRequestAt: string | null;
  /** 연결 (v2.143.0) — 영업진행 건 · 제안사 업체(거래처) · 제안사 담당자(주소록). 없으면 빈 값/null */
  salesId: string;
  salesProject: string;
  salesStatus: string;
  tier2CompanyId: number | null;
  tier2CompanyName: string;
  tier2ContactId: number | null;
  /** 「이름 직급」 */
  tier2ContactName: string;
  tier2ContactEmail: string;
  tier2ContactMobile: string;
  /** 담당자가 미연결 회사 소속이면 그 회사 글자 */
  tier2ContactCompany: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * 최근 변경 (v2.306.0) — 사람이 마지막으로 **실제로 바꾼** 칸. 「최근 갱신」(lastUpdate)은 저장을 누른 날이라
   * 아무것도 안 고쳐도 바뀐다. 서버 판정은 backend lib/deal-registration/change-summary.ts 한 곳. 옛 서버면 없음.
   */
  lastChange?: DealLastChange | null;
  /** 고치기 충돌 판정용 판(마지막 수정 시각의 마이크로초) — 고치기 창이 저장할 때 그대로 돌려보낸다 (v2.229.3) */
  rowVer?: string;
}

export interface DealLastChange {
  /** 'YYYY-MM-DD HH:MM' */
  at: string;
  by: string;
  byName: string;
  fields: string[];
  /** 사람 말 — 상태가 들었으면 맨 앞 */
  labels: string[];
  statusChanged: boolean;
}

/** 딜 변경 이력 한 줄 — GET /deal-registrations/:id/audit (v2.306.0) */
export interface DealAuditRow {
  logId: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  fieldName: string | null;
  label: string;
  oldValue: string | null;
  newValue: string | null;
  userId: string;
  userName: string;
  /** 사람 없이 바뀐 것(자동 연장·자료 정리) */
  auto: boolean;
  /** 'YYYY-MM-DD HH:MM:SS' */
  changedAt: string;
}

export interface DealListResult {
  rows: DealRow[];
  counts: {
    tracking: number;
    closed: number;
    byYear: { year: number | null; count: number }[];
  };
}

export interface DealMeta {
  vendors: { id: string; label: string }[];
  statuses: { id: DealStatus; label: string; closed: boolean }[];
  trackingStatuses: DealStatus[];
  closedStatuses: DealStatus[];
  applications: { group: string; items: string[] }[];
  employees: { empId: string; name: string; department: string }[];
  /** 날짜 기본값 규칙 — 만료 = 신청 + expireMonths 개월, 마감 = 만료 + deadlineDays 일 */
  rule: { expireMonths: number; deadlineDays: number };
}

export interface DealInput {
  vendor: string;
  registrationNo: string;
  projectId: string;
  status: DealStatus;
  appliedDate: string;
  expireDate: string;
  deadlineDate: string;
  closedDate: string;
  tier2: string;
  /**
   * 엔드유저 세 칸 (v2.193.0). ⛔**글자 칸(`endUser`)은 보내지 않는다** — 서버가 세 칸으로 다시 만든다.
   * 보내면 세 칸을 모두 비웠을 때 옛 글자가 되살아난다.
   */
  endUserKo: string;
  endUserEn: string;
  endUserUrl: string;
  projectInfo: string;
  /** 프로젝트명(Topic) + 영문 — 인포트렌드 메일에 「한글 / English」로 나란히 들어간다 (v2.189.0) */
  topic: string;
  topicEn: string;
  projectInfoEn: string;
  pn: string;
  /** 품목 줄 — 품목 칸·수량 칸 (v2.178.0). 이쪽이 원본이고 pn 은 서버가 여기서 만든 사본 */
  items: DealItem[];
  /**
   * ＋ 새 업데이트 (v2.194.0) — 저장하면 **이력에 한 줄 더** 쌓인다.
   * ⚠프로젝트 정보 칸을 덮지 않는다. 비우면 아무 일도 없다.
   */
  newUpdate: string;
  remark: string;
  application: string;
  empId: string;
  ownerName: string;
  note: string;
  /** 딜 금액(USD) — 입력칸의 글자 그대로(쉼표가 섞여도 서버가 떼고 받는다). 비우면 지운다 (v2.145.0) */
  amountUsd: string;
  /** 「정보 요청」에서 만든 딜이면 그 요청 번호 — 새로 넣을 때만 서버가 본다 (v2.141.0) */
  requestId?: number | null;
  /** 고치기 창을 열 때 받은 판 — 그 사이 남이 고쳤으면 서버가 409 로 거절한다(덮어쓰기 방지 · v2.229.3). 새 딜엔 없음 */
  rowVer?: string;
  /** 연결 (v2.143.0) — 비우면 연결 없음 */
  salesId: string;
  tier2CompanyId: number | null;
  tier2ContactId: number | null;
}

export type DealScope = 'tracking' | 'closed' | 'all';

export interface DealListParams {
  vendor: string;
  scope: DealScope;
  year?: number | null;
  q?: string;
  status?: DealStatus | '';
  empId?: string;
}

export async function listDeals(p: DealListParams): Promise<DealListResult> {
  const qs = new URLSearchParams();
  qs.set('vendor', p.vendor);
  qs.set('scope', p.scope);
  if (p.year) qs.set('year', String(p.year));
  if (p.q) qs.set('q', p.q);
  if (p.status) qs.set('status', p.status);
  if (p.empId) qs.set('empId', p.empId);
  return api.get<DealListResult>(`/deal-registrations?${qs.toString()}`);
}

export async function getDealMeta(): Promise<DealMeta> {
  return api.get<DealMeta>('/deal-registrations/meta');
}

export async function getDeal(id: number): Promise<DealRow> {
  return api.get<DealRow>(`/deal-registrations/${id}`);
}

export async function getDealAudit(id: number): Promise<DealAuditRow[]> {
  return api.get<DealAuditRow[]>(`/deal-registrations/${id}/audit`);
}

/**
 * 만료 임박 — 대시보드용 (v2.142.0). 범위는 **서버가** 정한다:
 *   영업지원팀·관리자 = 전체('all') · 영업담당자 = 내 딜('mine') · 그 밖 = 없음('none').
 * eligible 이 false 면 대시보드에 아예 안 보인다(딜이 하나도 없는 사람에게 빈 칸을 보이지 않게).
 */
export interface ExpiringDeals {
  scope: 'all' | 'mine' | 'none';
  eligible: boolean;
  days: number;
  rows: DealRow[];
}

export async function listExpiringDeals(days = 30, vendor = 'infortrend'): Promise<ExpiringDeals> {
  return api.get<ExpiringDeals>(`/deal-registrations/expiring?vendor=${vendor}&days=${days}`);
}

export async function createDeal(input: DealInput): Promise<DealRow> {
  return api.post<DealRow>('/deal-registrations', input);
}

export async function updateDeal(id: number, input: DealInput): Promise<DealRow> {
  return api.patch<DealRow>(`/deal-registrations/${id}`, input);
}

export async function deleteDeal(id: number): Promise<void> {
  await api.delete<{ deleted: boolean }>(`/deal-registrations/${id}`);
}

/**
 * 상태만 바꾸기 — 목록의 상태 칸 드롭다운 (v2.215.0). 고친 딜을 통째로 돌려준다.
 * ⚠종료 상태로 바꿔도 종료 탭으로 넘어가지 않는다(넘기기는 [종료 탭으로 넘기기]).
 */
export async function setDealStatus(id: number, status: DealStatus): Promise<DealRow> {
  return api.patch<DealRow>(`/deal-registrations/${id}/status`, { status });
}

/** 종료 탭으로 넘길 수 있는 딜 — 진행 중 탭에 있는 종료 상태(수주·실주·무효·거절) 딜 (v2.215.0) */
export async function listArchiveCandidates(vendor = 'infortrend'): Promise<DealRow[]> {
  const r = await api.get<{ rows: DealRow[] }>(`/deal-registrations/archive/candidates?vendor=${vendor}`);
  return r.rows;
}

/** 골라서 종료 탭으로 넘기기 — 서버가 다시 걸러 진행 중인 딜은 건너뛴다(skipped) */
export async function archiveDeals(ids: number[]): Promise<{ moved: number; skipped: number[] }> {
  return api.post<{ moved: number; skipped: number[] }>('/deal-registrations/archive', { ids });
}

/* ── 프로젝트 정보 업데이트 이력 (v2.194.0) — 셋 다 고친 딜을 통째로 돌려준다(화면이 다시 안 불러도 된다) ── */
export async function addDealUpdate(id: number, body: string, date?: string): Promise<DealRow> {
  return api.post<DealRow>(`/deal-registrations/${id}/updates`, { body, ...(date ? { date } : {}) });
}

export async function editDealUpdate(id: number, updateId: number, body: string, date?: string): Promise<DealRow> {
  return api.patch<DealRow>(`/deal-registrations/${id}/updates/${updateId}`, { body, ...(date ? { date } : {}) });
}

export async function removeDealUpdate(id: number, updateId: number): Promise<DealRow> {
  return api.delete<DealRow>(`/deal-registrations/${id}/updates/${updateId}`);
}

/** 만료 안내 메일 초안 — 서버가 고정 안내문을 딜 내용으로 채워 준다 (본문 규칙은 서버 한 곳) */
export interface ExpiryMailDraft {
  /** 제안사 칸에서 찾은 메일 주소 — 없으면 빈 배열(창에서 적는다) */
  to: string[];
  /** 제목의 이름 자리 — mailSubject('딜등록 만료 안내', subjectName) 로 감싼다 */
  subjectName: string;
  body: string;
}

export async function getExpiryMailDraft(id: number): Promise<ExpiryMailDraft> {
  return api.get<ExpiryMailDraft>(`/deal-registrations/${id}/expiry-mail`);
}

/* ── 🔎 아는 영문 찾기 (v2.192.0 · 종전 「✨ 영문 자동 채우기」) ──
 *   ⭐**번역하지 않는다.** 우리가 과거 딜에 적어 둔 영문만 찾아 준다.
 *   ⛔모르면 그 칸은 `filled` 에 안 들어온다 — 화면은 **넘어온 칸만** 바꾼다. */

export type KnownField = 'topicEn' | 'projectInfoEn' | 'endUserEn' | 'tier2';

export interface FilledField {
  label: KnownField;
  /** 채울 값 — 엔드유저·제안사는 **영문 줄을 보탠 칸 전체** */
  value: string;
  /** 어디서 가져왔나 (사람이 읽는 문장) */
  how: string;
}

export interface KnownEnglishOutcome {
  filled: FilledField[];
  /** 채우진 못했지만 글 안에서 찾은 아는 이름 */
  hints: { ko: string; en: string }[];
  /** 끝내 모르는 칸 — 「직접 적어 주세요」 */
  missing: string[];
  /** 뒤진 이름 수 */
  dictSize: number;
}

/** 우리가 전에 적어 둔 영문을 찾아 돌려준다 — **칸에 채우기만** 하고 저장은 사람이 한다 */
export async function findKnownEnglish(id: number): Promise<KnownEnglishOutcome> {
  return api.post<KnownEnglishOutcome>(`/deal-registrations/${id}/known-english`, {});
}

/* ── 딜등록 안내 메일 2종 (v2.190.0) ── */

export type NoticeTarget = 'infortrend' | 'tier2';

export interface NoticeMailDraft {
  to: string[];
  cc: string[];
  subjectName: string;
  /** 표가 든 서식 본문 — 메일 창에 그대로 넣는다 */
  doc: RichDoc;
  /** 못 보내는 이유 (빈 문자열이면 보낼 수 있다) — 화면이 이 문장을 그대로 보여 준다 */
  blocked: string;
}

/** 딜등록 안내 메일 초안 — 인포트렌드(한/영)와 제안사(한글)의 내용이 서로 다르다 */
export async function getNoticeMailDraft(id: number, target: NoticeTarget): Promise<NoticeMailDraft> {
  return api.get<NoticeMailDraft>(`/deal-registrations/${id}/notice-mail?target=${target}`);
}

/** 발송이 끝난 뒤 — 딜에 「보냈다」 날짜와 경과 한 줄을 적는다 */
export async function markExpiryMailSent(id: number, to: string[]): Promise<DealRow> {
  return api.post<DealRow>(`/deal-registrations/${id}/expiry-mail-sent`, { to });
}

/* ── 만료 안내 (v2.150.0 · ⭐v2.215.0 부터 매월 20일 안내 · 24일 검토 표 · 25일 상태표) ── */

/** 월간 안내 차례 — 20일 안내 글 · 24일 검토 표(그림) · 25일 상태표(엑셀) */
export type NoticeKind = 'd20' | 'd24' | 'd25';

/** 시험발송 결과 — 실제 안내는 그 날 09:00 에 따로 나간다(이 호출은 발송 기록을 건드리지 않는다) */
export interface ExpiryAlertTestResult {
  sent: boolean;
  /** 못 보낸 이유 (봇 미설정 · 메시지방 번호 미설정 · 웍스가 거절 등) */
  reason?: string;
  dealCount: number;
  kind?: NoticeKind;
  /** v2.162.0 — 딜등록 **메시지방**에 올린다(개인 1:1 이 아니다) */
  channel?: boolean;
}

/** 월간 안내 시험발송 — 관리자만. **딜등록 메시지방에** 올린다(맨 앞에 「[시험발송]」).
 *  ⚠방 사람들이 다 본다. 차례는 주소 뒤(?kind=)로 — 본문 없는 POST 다
 *  (본문 없이 content-type 을 붙이면 서버가 400 을 낸다 — api.post 는 안 붙인다) */
export async function sendExpiryAlertTest(kind: NoticeKind = 'd20'): Promise<ExpiryAlertTestResult> {
  return api.post<ExpiryAlertTestResult>(`/deal-registrations/expiry-alert/test?kind=${kind}`);
}

/**
 * 24일 검토 표(그림) · 25일 상태표(엑셀) **미리보기** — 방에 올리지 않고 나만 받는다 (v2.215.0).
 * ⚠api 래퍼는 JSON 만 다뤄서 fetch 로 직접 받는다. 대상이 없으면 서버가 404 와 이유를 준다.
 */
export async function fetchMonthlyPreview(kind: 'd24' | 'd25'): Promise<{ blob: Blob; name: string; count: number | null }> {
  const res = await fetch(`/api/deal-registrations/monthly-notice/preview?kind=${kind}`, { credentials: 'include' });
  if (!res.ok) {
    const json = await res.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(json?.error?.message ?? `미리보기를 만들지 못했습니다 (HTTP ${res.status})`);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  let name = kind === 'd24' ? '딜등록_만료예정.png' : '딜등록_만료예정.xlsx';
  if (star?.[1]) { try { name = decodeURIComponent(star[1].trim()); } catch { /* 기본 이름 */ } }
  // 들어간 딜 수 (v2.219.0) — 없거나 숫자가 아니면 null(알림 띠에서 뺀다)
  const n = Number(res.headers.get('X-Deal-Count') ?? '');
  return { blob: await res.blob(), name, count: res.headers.has('X-Deal-Count') && Number.isFinite(n) ? n : null };
}

/* ── 제안사(Tier 2) 담당자 명단 — v2.143.0 부터 거래처 담당자 주소록(lib/partner-contact-api.ts · /partner-contacts) 한 곳 ── */

/** 어느 영업건에 연결된 딜들 — 영업진행 상세 창의 「🤝 딜등록」 줄 (v2.143.0) */
export async function listDealsBySales(salesId: string): Promise<DealRow[]> {
  const r = await api.get<{ rows: DealRow[] }>(`/deal-registrations/by-sales/${encodeURIComponent(salesId)}`);
  return r.rows;
}

/* ── 딜등록 정보 요청 메일 (v2.141.0) ── */

/** 요청 창에서 적는 값 — 서버가 고정 안내문과 양식(xlsx)을 이 값으로 채운다 */
export interface DealRequestInput {
  vendor: string;
  contactId: number | null;
  company: string;
  contactName: string;
  email: string;
  phone: string;
  endUser: string;
  projectName: string;
  projectStatus: string;
}

export interface DealRequestDraft {
  to: string[];
  /** mailSubject('딜등록 정보 요청', subjectName) */
  subjectName: string;
  body: string;
}

export interface DealRequestRow {
  id: number;
  vendor: string;
  contactId: number | null;
  company: string;
  contactName: string;
  toEmails: string[];
  endUser: string;
  /** 엔드유저를 세 칸으로 가른 것 — **서버가 갈라 준다**(v2.193.0 · 새 딜 입력 창을 채우는 데 쓴다) */
  endUserKo: string;
  endUserEn: string;
  endUserUrl: string;
  projectName: string;
  projectStatus: string;
  /** 이 요청으로 만든 딜등록 — 없으면 null */
  dealId: number | null;
  dealLabel: string;
  sentAt: string;
  sentBy: string;
  sentByName: string;
  note: string;
}

export async function getRequestDraft(input: DealRequestInput): Promise<DealRequestDraft> {
  return api.post<DealRequestDraft>('/deal-registrations/requests/draft', input);
}

/**
 * 채운 「딜등록정보요청」 양식을 파일로 받는다 — 메일 창의 첨부 자리에 넣는다.
 * ⚠api 래퍼는 JSON 만 다뤄서 fetch 로 직접 받는다. 이름은 서버가 정한다(filename*).
 */
export async function fetchRequestForm(input: DealRequestInput): Promise<File> {
  const res = await fetch('/api/deal-registrations/requests/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(json?.error?.message ?? `양식을 만들지 못했습니다 (HTTP ${res.status})`);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  let name = '딜등록정보요청.xlsx';
  if (star?.[1]) { try { name = decodeURIComponent(star[1].trim()); } catch { /* 기본 이름 */ } }
  const blob = await res.blob();
  return new File([blob], name, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export async function listRequests(linked: 'no' | 'all', vendor = 'infortrend'): Promise<{ rows: DealRequestRow[]; counts: { unlinked: number; total: number } }> {
  return api.get<{ rows: DealRequestRow[]; counts: { unlinked: number; total: number } }>(`/deal-registrations/requests?vendor=${vendor}&linked=${linked}`);
}

/** 메일 창이 발송에 성공한 뒤 — 「보냈다」 기록 */
export async function createRequest(input: DealRequestInput & { toEmails: string[]; dealId: number | null }): Promise<DealRequestRow> {
  return api.post<DealRequestRow>('/deal-registrations/requests', input);
}

export async function deleteRequest(id: number): Promise<void> {
  await api.delete<{ deleted: boolean }>(`/deal-registrations/requests/${id}`);
}

/* ── 만료 안내 문구 — 화면에서 고친다 (v2.147.0) ────────────────────────
 *   본문은 「앞 문구 → 딜 정보 줄(자동) → 뒤 문구」 세 덩이다. 사람이 고치는 것은 앞뒤 문구뿐.
 *   미리보기는 **서버가 실제 조립 함수로** 만들어 준다 — 글 만드는 자리를 화면에 또 만들지 않는다.
 */
export interface ExpiryTemplate {
  intro: string;
  closing: string;
  /** 마지막으로 고친 때 — 한 번도 안 고쳤으면 null */
  savedAt: string | null;
  /** 지금 기본 문구를 쓰고 있나 */
  isDefault: boolean;
  defaults: { intro: string; closing: string };
  vars: { name: string; what: string }[];
  /** 고칠 수 있는 사람인가 (경영지원·관리자) */
  canEdit: boolean;
}

export async function getExpiryTemplate(): Promise<ExpiryTemplate> {
  return api.get<ExpiryTemplate>('/deal-registrations/expiry-mail/template');
}

export async function saveExpiryTemplate(input: { intro: string; closing: string }): Promise<ExpiryTemplate> {
  return api.put<ExpiryTemplate>('/deal-registrations/expiry-mail/template', input);
}

export async function previewExpiryTemplate(input: { intro: string; closing: string }): Promise<{ body: string; subjectName: string }> {
  return api.post<{ body: string; subjectName: string }>('/deal-registrations/expiry-mail/preview', input);
}

/* ───── 업데이트 문구 목록 (v2.216.0) ─────
 *   「쓸 말 생각 안 날 때 대분류 → 문구 골라 넣기」. 글 한 덩이를 서버가 대분류 묶음으로 나눠 준다 —
 *   나누는 규칙(lib/deal-registration/update-phrases.ts)을 화면에 베끼지 않는다. */
export interface UpdatePhrase {
  /** 칸에 들어갈 글자 — 「한글 / English」 (● 표시·번호는 뺀 것) */
  text: string;
  ko: string;
  en: string;
  /** ● 범용형 — 어느 딜에나 무난 */
  generic: boolean;
}
export interface UpdatePhraseGroup { key: string; title: string; phrases: UpdatePhrase[] }
export interface UpdatePhrases {
  /** 글 통째로 — 고치는 창이 쓴다 */
  text: string;
  groups: UpdatePhraseGroup[];
  isDefault: boolean;
  savedAt: string | null;
  defaultText: string;
  /** 연장 요청 줄의 머리 「Request for extend.」 */
  extendPrefix: string;
  /** 고칠 수 있는 사람인가 (경영지원·관리자) */
  canEdit: boolean;
}

export async function getUpdatePhrases(): Promise<UpdatePhrases> {
  return api.get<UpdatePhrases>('/deal-registrations/update-phrases');
}

/** 빈 글 = 기본 목록으로 되돌리기 */
export async function saveUpdatePhrases(text: string): Promise<UpdatePhrases> {
  return api.put<UpdatePhrases>('/deal-registrations/update-phrases', { text });
}
